import { marked } from 'marked';
import TurndownService from 'turndown';

// Configure marked for better TipTap compatibility
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Configure Turndown for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx', // Use # style headings
  codeBlockStyle: 'fenced', // Use ``` style code blocks
  bulletListMarker: '-', // Use - for bullet lists
});

/**
 * Convert Markdown to HTML for TipTap editor
 *
 * The article sections are stored in Markdown format from the backend,
 * but TipTap expects HTML. This function handles the conversion.
 *
 * @param markdown - The markdown string to convert
 * @returns HTML string suitable for TipTap
 */
export function markdownToHtml(markdown: string): string {
  try {
    return marked.parse(markdown) as string;
  } catch (error) {
    console.error('Failed to parse markdown:', error);
    // Return original markdown as fallback
    return markdown;
  }
}

/**
 * Convert HTML back to Markdown
 *
 * Used when saving TipTap editor content back to Markdown format.
 *
 * @param html - The HTML string to convert
 * @returns Markdown string
 */
export function htmlToMarkdown(html: string): string {
  try {
    return turndownService.turndown(html);
  } catch (error) {
    console.error('Failed to convert HTML to markdown:', error);
    // Return original HTML as fallback
    return html;
  }
}

/**
 * Strip a single outer ```markdown / ```md / bare ``` wrapper from LLM output.
 *
 * Some prompts (notably extract_insights_sys.yaml) demonstrate the desired
 * output inside a fenced code block, and the LLM occasionally echoes the
 * fence in its response. ReactMarkdown then renders the whole payload as a
 * `<pre>` block. This unwraps that single outer wrapper so the body renders
 * as flowing markdown.
 *
 * Cases:
 *   "```markdown\n## H\nx\n```"        -> "## H\nx"          (stripped)
 *   "```md\n## H\nx\n```"              -> "## H\nx"          (stripped)
 *   "```\n## H\nx\n```"                -> "## H\nx"          (stripped, bare fence)
 *   "```MARKDOWN\n## H\nx\n```"        -> "## H\nx"          (case-insensitive)
 *   "```markdown\n```js\ny\n```\n```"  -> "```js\ny\n```"    (inner fence preserved)
 *   "## H\nx"                          -> "## H\nx"          (no fence: unchanged)
 *   "```markdown\n## H\nx"             -> "```markdown\n..." (opener only: unchanged)
 *   "```js\nconsole.log(1)\n```"       -> "```js\n..."       (non-allowlisted lang: unchanged)
 *   "```markdown\n\n```"               -> "```markdown\n\n```" (empty inner: unchanged)
 */
export function stripCodeFences(content: string): string {
  const trimmed = content.trim();
  // Both regexes are anchored and contain no nested or lazy quantifiers,
  // so matching is linear-time and not susceptible to catastrophic backtracking.
  const openMatch = /^```([a-zA-Z]*)\n/.exec(trimmed);
  if (!openMatch) return content;
  const lang = openMatch[1].toLowerCase();
  if (lang !== '' && lang !== 'markdown' && lang !== 'md') return content;
  if (!/\n```\s*$/.test(trimmed)) return content;
  const closeIdx = trimmed.lastIndexOf('\n```');
  const inner = trimmed.slice(openMatch[0].length, closeIdx).trim();
  return inner || content;
}